<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Puntos de expedición</h2>
        <p class="text-muted mb-0">Cada punto representa un lugar de facturación dentro del establecimiento.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadAll" :disabled="loading">Recargar</button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <form class="row g-3" @submit.prevent="saveRow">
          <div class="col-md-3">
            <label class="form-label">Establecimiento</label>
            <select v-model.number="form.establecimiento_id" class="form-select" @change="handleEstablishmentChange">
              <option v-for="row in establishments" :key="row.establecimiento_id" :value="row.establecimiento_id">
                {{ row.codigo_establecimiento }} · {{ row.nombre_establecimiento }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Código punto</label>
            <input v-model="form.codigo_punto_expedicion" class="form-control" type="text" maxlength="3" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Nombre</label>
            <input v-model="form.nombre" class="form-control" type="text" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Activo</label>
            <select v-model="form.activo" class="form-select">
              <option :value="true">Sí</option>
              <option :value="false">No</option>
            </select>
          </div>
          <div class="col-12 d-flex gap-2">
            <button class="btn btn-dark" :disabled="saving || !form.establecimiento_id">{{ saving ? 'Guardando...' : submitLabel }}</button>
            <button class="btn btn-outline-secondary" type="button" @click="resetForm">Limpiar</button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Establecimiento</th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Activo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="6" class="text-center text-muted py-4">Cargando...</td>
            </tr>
            <tr v-else-if="!rows.length">
              <td colspan="6" class="text-center text-muted py-4">No hay puntos registrados.</td>
            </tr>
            <tr v-for="row in rows" :key="row.punto_id">
              <td>{{ row.punto_id }}</td>
              <td>{{ row.codigo_establecimiento }} · {{ row.nombre_establecimiento }}</td>
              <td>{{ row.codigo_punto_expedicion }}</td>
              <td>{{ row.nombre }}</td>
              <td>{{ row.activo ? 'Sí' : 'No' }}</td>
              <td class="text-end">
                <button class="btn btn-outline-dark btn-sm" @click="editRow(row)">Editar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const establishments = ref([])
const form = reactive({
  punto_id: null,
  establecimiento_id: null,
  codigo_punto_expedicion: '001',
  nombre: '',
  activo: true
})

const submitLabel = computed(() => form.punto_id ? 'Actualizar punto' : 'Crear punto')

function resetForm() {
  form.punto_id = null
  form.establecimiento_id = establishments.value[0]?.establecimiento_id || null
  form.codigo_punto_expedicion = '001'
  form.nombre = ''
  form.activo = true
}

function editRow(row) {
  form.punto_id = row.punto_id
  form.establecimiento_id = row.establecimiento_id
  form.codigo_punto_expedicion = row.codigo_punto_expedicion || ''
  form.nombre = row.nombre || ''
  form.activo = row.activo !== false
}

async function loadRows() {
  rows.value = []
  for (const establishment of establishments.value) {
    const points = await maintenanceFiscalService.listPoints(establishment.establecimiento_id)
    rows.value.push(...(points || []))
  }
}

async function loadAll() {
  loading.value = true
  try {
    establishments.value = await maintenanceFiscalService.listEstablishments() || []
    if (!form.establecimiento_id && establishments.value.length) {
      form.establecimiento_id = establishments.value[0].establecimiento_id
    }
    await loadRows()
  } catch (error) {
    console.error('Error cargando puntos', error)
    window.alert(error.userMessage || 'No se pudieron cargar los puntos')
  } finally {
    loading.value = false
  }
}

function handleEstablishmentChange() {
  if (!form.punto_id) {
    form.codigo_punto_expedicion = '001'
  }
}

async function saveRow() {
  saving.value = true
  try {
    const payload = {
      codigo_punto_expedicion: form.codigo_punto_expedicion,
      nombre: form.nombre,
      activo: form.activo
    }
    if (form.punto_id) {
      await maintenanceFiscalService.updatePoint(form.punto_id, payload)
    } else {
      await maintenanceFiscalService.createPoint(form.establecimiento_id, payload)
    }
    resetForm()
    await loadAll()
  } catch (error) {
    console.error('Error guardando punto', error)
    window.alert(error.userMessage || 'No se pudo guardar el punto')
  } finally {
    saving.value = false
  }
}

onMounted(loadAll)
</script>
